import { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, HelpCircle, Terminal, XCircle,
} from 'lucide-react';

/**
 * The evidence pane: the hero of the bench.
 *
 * The primary object here is the PAIR, not the row. A finding is the request
 * that behaved normally, the request that did not, and the number that says the
 * difference is real. Rendering those as three separate list items would make
 * the operator re-do the comparison the probe already did.
 *
 * The negative control is given as much space as the separation score on
 * purpose. A separation of 1.00 with a failed control is not a finding, and the
 * layout should not let someone skim past that.
 */

const STATUS_TONE = (status) => {
  if (!status) return 'text-slate-500';
  if (status >= 500) return 'text-red-700';
  if (status >= 400) return 'text-amber-700';
  return 'text-wasabi-700';
};

function ControlBadge({ passed }) {
  // Three states, not two. Null means no control was run, which is different
  // from a control that ran and misbehaved; collapsing them would let an
  // unverifiable probe read as a verified one.
  if (passed === true) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-wasabi-200 bg-wasabi-50 px-2 py-0.5 text-[11px] font-semibold text-wasabi-700">
        <CheckCircle2 className="h-3 w-3" /> control passed
      </span>
    );
  }
  if (passed === false) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
        <XCircle className="h-3 w-3" /> control FAILED
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
      <HelpCircle className="h-3 w-3" /> no control run
    </span>
  );
}

function separationTone(sep, controlPassed) {
  if (sep === null || sep === undefined) return 'text-slate-500';
  if (controlPassed === false) return 'text-slate-400 line-through';
  if (sep >= 1) return 'text-wasabi-700';
  if (sep >= 0.9) return 'text-amber-700';
  return 'text-slate-500';
}

/**
 * How many mutation rows to show before folding the rest away.
 *
 * A differential probe over a route inventory is 80+ requests and they are
 * nearly all the baseline. The interesting ones are the few that are NOT, so
 * the fold keeps every divergent row and hides the tail of identical ones.
 */
const ROWS_BEFORE_FOLD = 12;

/** One mutation row, as a line in a table. Expands to the body. */
function MutationRow({ row, baseline, index }) {
  const [open, setOpen] = useState(false);
  const req = row.request || {};
  const res = row.response || {};
  const base = (baseline?.response || {}).length;
  const differs = base !== undefined && res.length !== undefined && res.length !== base;

  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        className={`cursor-pointer border-t border-slate-100 transition
          ${differs ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'}`}
      >
        <td className="py-1 pl-2 pr-1 align-top text-slate-400">
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </td>
        <td className="max-w-[18rem] break-all py-1 pr-2 font-mono text-[11px] text-slate-900">
          {String(req.payload ?? '')}
        </td>
        <td className="py-1 pr-2 text-[11px] text-slate-500">{req.group || 'mutation'}</td>
        <td className={`py-1 pr-2 font-mono text-[11px] ${STATUS_TONE(res.status)}`}>
          {res.status ?? '-'}
        </td>
        <td className="py-1 pr-2 font-mono text-[11px] text-slate-700">{res.length ?? 0}b</td>
        {/* THE COLUMN THE WHOLE PANE EXISTS FOR. On a catch-all host every
            status is 200 and the only thing that separates a real endpoint
            from the shell is this number. "same" is the baseline; anything
            else is the signal, and the row is tinted so it cannot be
            skimmed past. */}
        <td className="py-1 pr-2 font-mono text-[11px]">
          {base === undefined ? (
            <span className="text-slate-400">-</span>
          ) : differs ? (
            <span className="font-semibold text-amber-800">
              {res.length > base ? '+' : ''}{res.length - base}
            </span>
          ) : (
            <span className="text-slate-400">same</span>
          )}
        </td>
        <td className="py-1 pr-2 text-right font-mono text-[11px] text-slate-400">
          {res.elapsed_ms ?? 0}ms
        </td>
      </tr>
      {open && (
        <tr className="border-t border-slate-100 bg-slate-50/60">
          <td />
          <td colSpan={6} className="px-2 py-2">
            {req.identity && (
              <div className="mb-1 text-[11px] text-slate-500">as {req.identity}</div>
            )}
            {res.excerpt ? (
              <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-white p-2 font-mono text-[11px] leading-relaxed text-slate-700">
                {res.excerpt}
              </pre>
            ) : (
              <div className="text-[11px] text-slate-500">No body captured for this request.</div>
            )}
            {res.error && <div className="mt-1 text-[11px] text-red-700">error: {res.error}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

function ProbeMatrix({ matrix, pairs, index }) {
  const sep = matrix.oracle_separation;
  const control = pairs.find((p) => (p.request || {}).group === 'control');
  const others = pairs.filter((p) => p !== control);

  const [showBaseline, setShowBaseline] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const baseLength = (control?.response || {}).length;
  const divergent = others.filter((r) => {
    const l = (r.response || {}).length;
    return baseLength !== undefined && l !== undefined && l !== baseLength;
  });

  // KEEP EVERY DIVERGENT ROW, fold the identical tail. Folding by position
  // would hide the one row that matters behind eleven that do not.
  const identical = others.filter((r) => !divergent.includes(r));
  const shown = showAll
    ? others
    : [...divergent, ...identical.slice(0, Math.max(0, ROWS_BEFORE_FOLD - divergent.length))];
  const hidden = others.length - shown.length;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Separation
          </div>
          <div className={`font-mono text-2xl font-semibold ${separationTone(sep, matrix.negative_control_passed)}`}>
            {sep === null || sep === undefined ? '--' : Number(sep).toFixed(2)}
          </div>
        </div>
        <div className="space-y-1">
          <ControlBadge passed={matrix.negative_control_passed} />
          <div className="text-[11px] text-slate-500">
            oracle <span className="font-mono">{matrix.oracle_kind || 'unknown'}</span>
          </div>
        </div>
        {matrix.interpretation && (
          <p className="min-w-[16rem] flex-1 text-xs leading-snug text-slate-600">
            {matrix.interpretation}
          </p>
        )}
      </div>

      {/* THE BASELINE, ONCE.
          It used to be re-rendered beside EVERY mutation row, so a probe over
          eighteen payloads printed the same control card eighteen times and the
          operator scrolled past a screen and a half of identical HTML to reach
          the tool output underneath. His words: "the baseline here is too
          much".

          It still has to be here - a mutation means nothing without the thing
          it is being compared against - so it is stated once, with its numbers
          on the lid, and every row below carries a delta against it. */}
      {control && (
        <div className="mb-2 rounded-md border border-slate-200 bg-slate-50">
          <button
            type="button"
            data-testid="evidence-baseline-toggle"
            aria-expanded={showBaseline}
            onClick={() => setShowBaseline((v) => !v)}
            className="flex w-full items-center gap-2 px-2 py-1.5 text-left"
          >
            {showBaseline
              ? <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
              : <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />}
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Baseline / control
            </span>
            <span className="truncate font-mono text-[11px] text-slate-700">
              {String((control.request || {}).payload ?? '')}
            </span>
            <span className={`ml-auto shrink-0 font-mono text-[11px] ${STATUS_TONE((control.response || {}).status)}`}>
              {(control.response || {}).status ?? '-'} &middot;{' '}
              {(control.response || {}).length ?? 0}b &middot;{' '}
              {(control.response || {}).elapsed_ms ?? 0}ms
            </span>
          </button>
          {showBaseline && (
            <div className="border-t border-slate-200 px-2 py-2 motion-safe:animate-panel-arrive">
              {(control.response || {}).excerpt ? (
                <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-white p-2 font-mono text-[11px] leading-relaxed text-slate-700">
                  {(control.response || {}).excerpt}
                </pre>
              ) : (
                <div className="text-[11px] text-slate-500">No body captured.</div>
              )}
            </div>
          )}
        </div>
      )}

      {others.length > 0 && (
        <>
          <div className="mb-1 flex items-center gap-2 text-[11px] text-slate-500">
            <span>
              <span className="font-semibold text-slate-700">{others.length}</span> request
              {others.length === 1 ? '' : 's'} against that baseline
            </span>
            {divergent.length > 0 && (
              <span className="rounded-full bg-amber-100 px-1.5 font-semibold text-amber-800">
                {divergent.length} differ
              </span>
            )}
            <span className="ml-auto text-slate-400">click a row for the body</span>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="w-5" />
                  <th className="py-1 pr-2 font-bold">Payload</th>
                  <th className="py-1 pr-2 font-bold">Group</th>
                  <th className="py-1 pr-2 font-bold">Status</th>
                  <th className="py-1 pr-2 font-bold">Length</th>
                  <th className="py-1 pr-2 font-bold">vs base</th>
                  <th className="py-1 pr-2 text-right font-bold">Time</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row, i) => (
                  <MutationRow key={row.id} row={row} baseline={control} index={i} />
                ))}
              </tbody>
            </table>
          </div>
          {hidden > 0 && (
            <button
              type="button"
              data-testid="evidence-show-all"
              onClick={() => setShowAll(true)}
              className="mt-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
            >
              Show {hidden} more identical response{hidden === 1 ? '' : 's'}
            </button>
          )}
          {showAll && hidden === 0 && others.length > ROWS_BEFORE_FOLD && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="mt-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
            >
              Collapse the identical responses
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ToolCard({ row }) {
  const req = row.request || {};
  const excerpt = (row.response || {}).excerpt || '';
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-900">
        <Terminal className="h-3.5 w-3.5 text-slate-400" />
        <span className="font-mono">
          {req.binary} {(req.args || []).join(' ')}
        </span>
      </div>
      <div className="text-[11px] text-slate-500">{req.target}</div>
      {excerpt && (
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-700">
          {excerpt}
        </pre>
      )}
    </div>
  );
}

export default function EvidencePane({ evidence }) {
  if (!evidence || evidence.length === 0) {
    return (
      <div className="flex min-h-[20rem] flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 p-8 text-center">
        <p className="text-sm font-medium text-slate-600">
          No evidence captured yet
        </p>
        <p className="mx-auto mt-1 max-w-md text-xs text-slate-500">
          This is what the target actually said: every request paired against
          its control, with the oracle numbers behind each claim. It fills in
          as the agent probes. For the agent&rsquo;s reasoning rather than its
          measurements, use Stream.
        </p>
      </div>
    );
  }

  // Group observations under the matrix they came from. Rows land in capture
  // order and a matrix always precedes its observations.
  const groups = [];
  let current = null;
  const loose = [];

  evidence.forEach((row) => {
    if (row.kind === 'probe_matrix') {
      current = { matrix: row, pairs: [] };
      groups.push(current);
    } else if (row.kind === 'request_pair' && current) {
      current.pairs.push(row);
    } else if (row.kind === 'tool_output') {
      loose.push(row);
    } else if (row.kind === 'observation' && row.interpretation) {
      loose.push(row);
    }
  });

  return (
    <div className="space-y-3">
      {groups.map((g, i) => (
        <div key={g.matrix.id}>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Probe {i + 1}
          </div>
          <ProbeMatrix matrix={g.matrix} pairs={g.pairs} index={i} />
        </div>
      ))}
      {loose.map((row) =>
        row.kind === 'tool_output' ? (
          <ToolCard key={row.id} row={row} />
        ) : (
          <div
            key={row.id}
            className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            {row.interpretation}
          </div>
        ),
      )}
    </div>
  );
}
